//
// # EditLayoutPinDialog
//
// Modal dialog for editing an existing layout pin's properties.
// Fields: Name, Layer, Pin Type. Pre-populated with current pin data.
//

import React, { useState, useCallback } from "react";
import { Modal } from "./Modal/Modal";
import { schema } from "GenAlphaSchema";

// Derive pin type options from the schema PinType constant.
const PIN_TYPE_OPTIONS = Object.entries(schema.core.PinType).map(
  ([label, value]) => ({ value, label })
);

// The result returned when the user confirms the dialog.
export interface EditLayoutPinConfig {
  readonly name: string;
  readonly layer: number;
  readonly pinType: schema.core.PinType;
}

interface EditLayoutPinDialogProps {
  readonly currentData: {
    readonly name: string;
    readonly layer: number;
    readonly pinType: schema.core.PinType | undefined;
  };
  readonly layers: ReadonlyArray<{
    readonly name: string;
    readonly number: number;
  }>;
  readonly onConfirm: (config: EditLayoutPinConfig) => void;
  readonly onCancel: () => void;
}

export function EditLayoutPinDialog({
  currentData,
  layers,
  onConfirm,
  onCancel,
}: EditLayoutPinDialogProps) {
  const [name, setName] = useState(currentData.name);
  const [layer, setLayer] = useState<number>(currentData.layer);
  const [pinType, setPinType] = useState<schema.core.PinType>(
    currentData.pinType ?? "signal"
  );

  const handleConfirm = useCallback(() => {
    onConfirm({ name, layer, pinType });
  }, [name, layer, pinType, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  return (
    <Modal open={true} onClose={onCancel} title="Edit Layout Pin">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Pin Name */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={inputStyle}
          />
        </div>

        {/* Layer */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Layer
          </label>
          <select
            value={layer}
            onChange={(e) => setLayer(Number(e.target.value))}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {layers.map((l) => (
              <option key={l.number} value={l.number}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        {/* Pin Type */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Pin Type
          </label>
          <select
            value={pinType}
            onChange={(e) => setPinType(e.target.value as schema.core.PinType)}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {PIN_TYPE_OPTIONS.map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor: "var(--color-ide-accent)",
              color: "#ffffff",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
